'use client';

import { useState } from 'react';
import { trackPostHogEvent } from '@/lib/analytics/posthog';
import { splitE164 } from '@/lib/phone/countries';
import { getLoadedPhoneLib } from '@/lib/phone/phone-lib';
import { isAcceptableWhatsapp } from '@/lib/phone/whatsapp-input';

/**
 * Estado y validación del formulario de titular (nombre / teléfono / email),
 * compartido por GuestPaymentModal (apartamentos) y TourGuestPaymentModal.
 * Validación on-blur (no on-change, evita estresar al usuario); los textos de
 * error son los mismos en ambos verticales.
 *
 * `fieldIdPrefix` permite que cada modal conserve sus ids de campo actuales
 * ('field-' en apartamentos, 'field-tour-' en tours) para el scroll-to-error.
 */
export interface GuestData {
  name: string;
  phone: string;
  email: string;
}

const EMPTY_GUEST: GuestData = { name: '', phone: '', email: '' };

export interface PaymentFormOptions {
  locale?: 'es' | 'en';
  /** Tours: email opcional (solo valida formato si lo escriben). Default false. */
  emailOptional?: boolean;
  /** Tours: 'Revisa el WhatsApp'. Default 'Revisa el teléfono'. */
  phoneErrorLabel?: string;
}

function nameError(value: string, en = false): string | null {
  const parts = value.trim().split(/\s+/).filter((p) => p.length > 0);
  if (!value || value.trim().length < 3) return en ? 'Enter your name' : 'Falta tu nombre';
  if (parts.length < 2) return en ? 'Include your first and last name' : 'Incluye nombre y apellido';
  return null;
}

function buildValidators(opts: PaymentFormOptions): Record<keyof GuestData, (v: string) => string | null> {
  const en = opts.locale === 'en';
  const phoneLabel =
    opts.phoneErrorLabel ??
    (en
      ? 'Check the number: choose your country or type it with + and the country code'
      : 'Revisa el número: elige tu país o escríbelo con + y el indicativo');
  return {
    name: (value) => nameError(value, en),
    // Número válido para su país (libphonenumber-js, cargada con el campo). La regla anterior
    // aceptaba «+57» + 6 dígitos o un número extranjero pegado detrás de «+57» (14-oct-2026).
    phone: (value: string) => (isAcceptableWhatsapp(value, getLoadedPhoneLib()) ? null : phoneLabel),
    email: (value: string) => {
      if (opts.emailOptional && value.trim() === '') return null;
      if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return en ? 'Check your email address' : 'Revisa tu correo';
      return null;
    },
  };
}

export function usePaymentForm(fieldIdPrefix: string = 'field-', options: PaymentFormOptions = {}) {
  const VALIDATORS = buildValidators(options);
  const [guestData, setGuestData] = useState<GuestData>(EMPTY_GUEST);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateField = (field: string, value: string): boolean => {
    const newErrors = { ...errors };
    const validator = VALIDATORS[field as keyof GuestData];
    if (!validator) return true;
    const error = validator(value);
    if (error) {
      newErrors[field] = error;
    } else {
      delete newErrors[field];
    }
    setErrors(newErrors);
    return !error;
  };

  const handleBlur = (field: keyof GuestData) => {
    setTouched((t) => ({ ...t, [field]: true }));
    validateField(field, guestData[field]);
  };

  const handleChange = (field: keyof GuestData, value: string) => {
    setGuestData((d) => ({ ...d, [field]: value }));
    // Si ya estaba "touched" y tenía error, re-validar para limpiar el error apenas se corrija
    if (touched[field] && errors[field]) {
      validateField(field, value);
    }
  };

  // Valida los 3 campos de forma síncrona; si hay error hace scroll+focus al primero.
  const validateAllAndScroll = (): boolean => {
    const fields: (keyof GuestData)[] = ['name', 'phone', 'email'];
    const newTouched: Record<string, boolean> = {};
    let firstErrorField: string | null = null;
    const newErrors: Record<string, string> = {};

    fields.forEach((f) => {
      newTouched[f] = true;
      const error = VALIDATORS[f](guestData[f]);
      if (error) newErrors[f] = error;
      if (newErrors[f] && !firstErrorField) firstErrorField = f;
    });

    setTouched(newTouched);
    setErrors(newErrors);

    if (firstErrorField) {
      // Señal para análisis de sesiones: qué campo bloqueó el envío y con qué país, sin PII.
      const phoneSplit = splitE164(guestData.phone);
      trackPostHogEvent('checkout_form_invalid', {
        form: fieldIdPrefix.replace(/-$/, ''),
        fields: Object.keys(newErrors).join(','),
        phone_dial: phoneSplit ? `+${phoneSplit.dial}` : undefined,
        phone_digits: guestData.phone.replace(/\D/g, '').length,
      });
      setTimeout(() => {
        const el = document.getElementById(`${fieldIdPrefix}${firstErrorField}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (el as HTMLInputElement).focus({ preventScroll: true });
        }
      }, 50);
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setGuestData(EMPTY_GUEST);
    setTouched({});
    setErrors({});
  };

  return {
    guestData,
    touched,
    errors,
    validateField,
    handleBlur,
    handleChange,
    validateAllAndScroll,
    resetForm,
  };
}
